import { Accordion, Button, CheckboxGroup, CustomDateTimePicker, Icon } from '@components/ui';
import { useOutsideAlerter } from '@hooks';
import clsx from 'clsx';
import dayjs from 'dayjs';
import isEqual from 'lodash.isequal';
import React, { useRef, useState } from 'react';
import { FiltersType, SelectedFiltersType } from 'src/pages/dashboard';

import { FilterTree } from '../FilterTree/FilterTree';

import styles from './Filters.module.scss';

interface FiltersProps {
  filters: FiltersType;
  selectedFilters: SelectedFiltersType;
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFiltersType>>;
  appliedFilters: SelectedFiltersType;
  setAppliedFilters: React.Dispatch<React.SetStateAction<SelectedFiltersType>>;
}

type FilterType = 'departments' | 'models' | 'useCases' | 'untilDate';

export const Filters: React.FC<FiltersProps> = ({
  appliedFilters,
  setAppliedFilters,
  filters,
  selectedFilters,
  setSelectedFilters,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  const filtersRef = useRef(null);
  useOutsideAlerter(filtersRef, setShowFilters.bind(null, false));

  const handleFilterSelect = (filterType: FilterType, items: string[] | Date) => {
    if (filterType === 'departments') {
      setSelectedFilters(prev => ({ ...prev, departments: items as string[] }));
    }
    if (filterType === 'models') {
      setSelectedFilters(prev => ({ ...prev, models: items as string[] }));
    }
    if (filterType === 'useCases') {
      setSelectedFilters(prev => ({ ...prev, useCases: items as string[] }));
    }
    if (filterType === 'untilDate') {
      setSelectedFilters(prev => ({ ...prev, untilDate: items as Date }));
    }
  };

  const applyFilters = () => {
    setAppliedFilters(selectedFilters); // apply selected filters
  };

  const isDirty = isEqual(selectedFilters, appliedFilters);

  return (
    <div
      ref={filtersRef}
      className={styles.filtersContainer}
    >
      <div
        onClick={setShowFilters.bind(null, !showFilters)}
        className={styles.label}
      >
        <span>Filters</span>
        <Icon
          className={styles.icon}
          name="filters"
        />
      </div>
      <div className={clsx(styles.popup, { [styles.show]: showFilters })}>
        <div className={styles.header}>
          <div className={styles.title}>Filters</div>
          {/* <div className={styles.controls}>

            <span>Save view</span>
          </div> */}
        </div>
        <div className={styles.filter}>
          <Accordion
            label="Departments"
            numberIndicator={selectedFilters.departments.length}
            className={styles.accordion}
          >
            <CheckboxGroup
              className={styles.checkboxGroup}
              defaultSelected={selectedFilters.departments}
              onSelect={keys => handleFilterSelect('departments', keys)}
              options={filters?.departments?.map(department => ({ id: department, name: department }))}
            />
          </Accordion>
          <Accordion
            label="Use cases"
            numberIndicator={selectedFilters.useCases.length + selectedFilters.models.length}
            className={styles.accordion}
          >
            <FilterTree
              onModelSelect={keys => handleFilterSelect('models', keys)}
              onUseCaseSelect={keys => handleFilterSelect('useCases', keys)}
              items={filters.useCases.map(useCase => ({
                item: { id: useCase.name, name: useCase.name },
                subitems: Array.from(new Set(useCase.models)).map(model => ({ id: model, name: model })),
              }))}
              selectedFilters={selectedFilters}
            />
          </Accordion>
          <Accordion
            label="Date"
            numberIndicator={!dayjs(selectedFilters.untilDate).isSame(new Date(), 'day') ? 1 : undefined}
            className={clsx(styles.accordion, styles.date)}
          >
            <CustomDateTimePicker
              name="untilDate"
              label="Set the cutoff date: Enter the end date for data calculation"
              dateValue={selectedFilters.untilDate}
              defaultValue={selectedFilters.untilDate}
              onSelect={(value: Date) => handleFilterSelect('untilDate', value)}
            />
          </Accordion>
          <Button
            className={styles.applyButton}
            view="primary"
            onClick={() => {
              applyFilters();
              setShowFilters(false);
            }}
            disabled={isDirty}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};
